import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { trackWhatsAppClick, trackEventCustom } from "../../utils/analytics";

export default function CRMLeadManagement() {
  useEffect(() => {
    document.title =
      "CRM & Lead Management Software Development Company in Pune | HexxaTech";

    const description =
      "HexxaTech develops custom CRM and lead management software in Pune for managing leads, customers, follow-ups, appointments, sales activities, pipelines, reports and business relationships.";

    const setMeta = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);

      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", name);
        document.head.appendChild(meta);
      }

      meta.setAttribute("content", content);
    };

    setMeta("description", description);

    let canonical = document.querySelector('link[rel="canonical"]');

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute(
      "href",
      "https://hexxatech.co.in/services/crm-lead-management"
    );

    const setPropertyMeta = (property, content) => {
      let meta = document.querySelector(`meta[property="${property}"]`);

      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("property", property);
        document.head.appendChild(meta);
      }

      meta.setAttribute("content", content);
    };

    setPropertyMeta(
      "og:title",
      "CRM & Lead Management Software Development | HexxaTech"
    );

    setPropertyMeta("og:description", description);
    setPropertyMeta(
      "og:url",
      "https://hexxatech.co.in/services/crm-lead-management"
    );
    setPropertyMeta("og:type", "website");
    setPropertyMeta("og:site_name", "HexxaTech Softwares");

    const existingSchema = document.getElementById(
      "hexxatech-crm-service-schema"
    );

    if (existingSchema) {
      existingSchema.remove();
    }

    const script = document.createElement("script");

    script.id = "hexxatech-crm-service-schema";
    script.type = "application/ld+json";

    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: "CRM & Lead Management Software Development",
      serviceType: "CRM Software Development",
      provider: {
        "@type": "Organization",
        name: "HexxaTech Softwares",
        url: "https://hexxatech.co.in/",
      },
      areaServed: [
        {
          "@type": "City",
          name: "Pune",
        },
        {
          "@type": "Country",
          name: "India",
        },
      ],
      description: description,
      url: "https://hexxatech.co.in/services/crm-lead-management",
    });

    document.head.appendChild(script);

    return () => {
      const schema = document.getElementById(
        "hexxatech-crm-service-schema"
      );

      if (schema) {
        schema.remove();
      }
    };
  }, []);

  const features = [
    {
      icon: "◎",
      title: "Lead Management",
      text: "Capture, organize and manage leads from different sources in one centralized CRM system.",
    },
    {
      icon: "◈",
      title: "Customer Management",
      text: "Maintain centralized customer information, communication history, requirements and business interactions.",
    },
    {
      icon: "↗",
      title: "Sales Pipeline",
      text: "Track opportunities through different sales stages and maintain clear visibility of your sales pipeline.",
    },
    {
      icon: "✓",
      title: "Follow-up Management",
      text: "Schedule and manage follow-ups so sales teams can stay organized and reduce missed opportunities.",
    },
    {
      icon: "▦",
      title: "Appointment Management",
      text: "Manage customer meetings, appointments, schedules and related sales activities from one platform.",
    },
    {
      icon: "◫",
      title: "Sales Activities",
      text: "Track calls, meetings, discussions, tasks and other activities associated with leads and customers.",
    },
    {
      icon: "⌁",
      title: "Reports & Dashboards",
      text: "Monitor leads, conversions, sales activities, follow-ups and performance through business dashboards.",
    },
    {
      icon: "⚙",
      title: "Workflow Automation",
      text: "Automate repetitive CRM processes, assignments, notifications and workflow-based business activities.",
    },
  ];

  const benefits = [
    {
      title: "Centralized customer information",
      text: "Keep customer and lead information organized in one system instead of scattered spreadsheets and applications.",
    },
    {
      title: "Better follow-up visibility",
      text: "Give your sales team clear visibility into pending, completed and upcoming follow-up activities.",
    },
    {
      title: "Improved sales tracking",
      text: "Track opportunities and sales activities from initial enquiry through conversion.",
    },
    {
      title: "Reduced manual work",
      text: "Automate repetitive data entry, assignments, reminders and workflow processes.",
    },
    {
      title: "Better management reporting",
      text: "Give business owners and managers access to meaningful sales and operational information.",
    },
    {
      title: "Built around your workflow",
      text: "The CRM can be customized around your actual business process, team structure and reporting requirements.",
    },
  ];

  const workflow = [
    "Lead Capture",
    "Lead Qualification",
    "Sales Assignment",
    "Follow-up",
    "Customer Interaction",
    "Quotation / Opportunity",
    "Conversion",
    "Reporting",
  ];

  const serviceNavigation = [
    { title: "Custom Software Development", to: "/services/custom-software-development" },
    { title: "ERP & Business Management", to: "/services/erp-business-management" },
    { title: "CRM & Lead Management", to: "/services/crm-lead-management" },
    { title: "Healthcare Software", to: "/services/healthcare-software" },
    { title: "Inventory Management", to: "/services/inventory-management" },
    { title: "Industrial Process Software", to: "/services/industrial-process-software" },
    { title: "License Management", to: "/services/license-management" },
    { title: "Mobile App Development", to: "/services/mobile-app-development" },
  ];

  const currentIndex = serviceNavigation.findIndex(
    (service) => service.to === "https://hexxatech.co.in/services/crm-lead-management"
  );
  const previousService =
    serviceNavigation[(currentIndex - 1 + serviceNavigation.length) % serviceNavigation.length];
  const nextService =
    serviceNavigation[(currentIndex + 1) % serviceNavigation.length];

  const industries = [
    "Manufacturing",
    "Automotive",
    "Healthcare",
    "Education",
    "Real Estate",
    "Trading & Distribution",
    "Professional Services",
    "B2B Businesses",
  ];

  return (
    <div className="hexxa-crm-page">
      <style>{`
        * {
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        body {
          margin: 0;
          font-family: Inter, -apple-system, BlinkMacSystemFont,
            "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          color: #0f172a;
          background: #ffffff;
        }

        .hexxa-crm-page {
          overflow: hidden;
          background: #ffffff;
        }

        .hexxa-container {
          width: min(1180px, calc(100% - 40px));
          margin: 0 auto;
        }

        /* =========================
           HERO
        ========================= */

        .crm-hero {
          position: relative;
          overflow: hidden;
          padding: 105px 0 90px;
          color: #ffffff;

          background:
            radial-gradient(
              circle at 82% 20%,
              rgba(59, 130, 246, 0.22),
              transparent 30%
            ),
            linear-gradient(
              135deg,
              #294a7d 0%,
              #355b91 55%,
              #294a7d 100%
            );
        }

        .crm-hero::before {
          content: "";
          position: absolute;
          width: 430px;
          height: 430px;
          border-radius: 50%;
          right: -160px;
          top: -220px;
          background: rgba(255,255,255,.035);
        }

        .crm-hero::after {
          content: "";
          position: absolute;
          width: 240px;
          height: 240px;
          border-radius: 50%;
          left: -130px;
          bottom: -150px;
          border: 1px solid rgba(255,255,255,.08);
        }

        .crm-hero-grid {
          position: relative;
          z-index: 2;
          display: grid;
          grid-template-columns: 1.05fr .95fr;
          gap: 70px;
          align-items: center;
        }

        .crm-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 12px;
          margin-bottom: 20px;
          border-radius: 999px;
          background: rgba(255,255,255,.10);
          border: 1px solid rgba(255,255,255,.16);
          color: #dbeafe;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.2px;
          text-transform: uppercase;
        }

        .crm-eyebrow-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #60a5fa;
        }

        .crm-hero h1 {
          max-width: 720px;
          margin: 0;
          font-size: clamp(42px, 5vw, 65px);
          line-height: 1.04;
          letter-spacing: -2.8px;
          font-weight: 800;
        }

        .crm-hero h1 span {
          color: #60a5fa;
        }

        .crm-hero-text {
          max-width: 650px;
          margin: 25px 0 30px;
          color: #dbeafe;
          font-size: 17px;
          line-height: 1.8;
        }

        .crm-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .crm-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
          padding: 0 20px;
          border-radius: 10px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 700;
          transition: .25s ease;
        }

        .crm-btn-primary {
          color: #ffffff;
          background: #2563eb;
          box-shadow: 0 12px 25px rgba(15,23,42,.20);
        }

        .crm-btn-primary:hover {
          transform: translateY(-2px);
          background: #1d4ed8;
        }

        .crm-btn-light {
          color: #ffffff;
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.22);
        }

        .crm-btn-light:hover {
          background: rgba(255,255,255,.15);
        }

        /* =========================
           CRM VISUAL
        ========================= */

        .crm-hero-visual {
          min-height: 380px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .crm-dashboard {
          width: min(480px, 100%);
          padding: 15px;
          border-radius: 22px;
          background: rgba(255,255,255,.10);
          border: 1px solid rgba(255,255,255,.18);
          backdrop-filter: blur(10px);
          box-shadow: 0 30px 70px rgba(15,23,42,.25);
          transform: rotate(-2deg);
        }

        .crm-dashboard-top {
          display: flex;
          gap: 6px;
          padding-bottom: 14px;
        }

        .crm-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(255,255,255,.60);
        }

        .crm-dashboard-content {
          display: grid;
          grid-template-columns: 72px 1fr;
          gap: 12px;
        }

        .crm-sidebar {
          min-height: 285px;
          padding: 12px 8px;
          border-radius: 12px;
          background: rgba(255,255,255,.10);
        }

        .crm-side-line {
          height: 8px;
          margin-bottom: 13px;
          border-radius: 5px;
          background: rgba(255,255,255,.18);
        }

        .crm-side-line.active {
          background: #60a5fa;
        }

        .crm-dashboard-main {
          display: grid;
          gap: 12px;
        }

        .crm-dashboard-title {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px;
          border-radius: 11px;
          background: rgba(255,255,255,.10);
        }

        .crm-dashboard-title strong {
          font-size: 13px;
        }

        .crm-dashboard-title span {
          font-size: 9px;
          color: #bfdbfe;
        }

        .crm-mini-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 9px;
        }

        .crm-mini-card {
          min-height: 72px;
          padding: 12px;
          border-radius: 11px;
          background: rgba(255,255,255,.10);
          border: 1px solid rgba(255,255,255,.08);
        }

        .crm-mini-card strong {
          display: block;
          margin-bottom: 5px;
          font-size: 18px;
        }

        .crm-mini-card small {
          color: #cbd5e1;
          font-size: 8px;
        }

        .crm-lead-list {
          display: grid;
          gap: 8px;
        }

        .crm-lead {
          display: grid;
          grid-template-columns: 30px 1fr auto;
          align-items: center;
          gap: 9px;
          padding: 9px;
          border-radius: 10px;
          background: rgba(255,255,255,.08);
        }

        .crm-lead-avatar {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: rgba(96,165,250,.25);
          color: #dbeafe;
          font-size: 9px;
          font-weight: 800;
        }

        .crm-lead-info strong {
          display: block;
          font-size: 9px;
        }

        .crm-lead-info small {
          color: #cbd5e1;
          font-size: 7px;
        }

        .crm-lead-status {
          padding: 4px 7px;
          border-radius: 999px;
          background: rgba(96,165,250,.18);
          color: #bfdbfe;
          font-size: 7px;
        }

        /* =========================
           QUICK POINTS
        ========================= */

        .crm-overview {
          position: relative;
          z-index: 3;
          margin-top: -35px;
        }

        .crm-overview-box {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          overflow: hidden;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 20px;
          box-shadow: 0 18px 50px rgba(15,23,42,.08);
        }

        .crm-overview-item {
          padding: 28px 22px;
          text-align: center;
          border-right: 1px solid #e5e7eb;
        }

        .crm-overview-item:last-child {
          border-right: 0;
        }

        .crm-overview-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 10px;
          border-radius: 11px;
          background: #eff6ff;
          color: #2563eb;
          font-size: 17px;
          font-weight: 800;
        }

        .crm-overview-item strong {
          display: block;
          font-size: 13px;
        }

        .crm-overview-item span {
          display: block;
          margin-top: 4px;
          color: #64748b;
          font-size: 11px;
        }

        /* =========================
           COMMON
        ========================= */

        .crm-section {
          padding: 90px 0;
        }

        .crm-section-soft {
          background: #f8fafc;
        }

        .crm-label {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        .crm-title {
          max-width: 780px;
          margin: 12px 0 0;
          color: #0f172a;
          font-size: clamp(32px, 4vw, 48px);
          line-height: 1.1;
          letter-spacing: -1.8px;
        }

        .crm-description {
          max-width: 720px;
          margin: 18px 0 0;
          color: #64748b;
          font-size: 16px;
          line-height: 1.8;
        }

        /* =========================
           INTRO
        ========================= */

        .crm-intro-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 70px;
          align-items: center;
        }

        .crm-intro-text {
          color: #64748b;
          font-size: 15px;
          line-height: 1.85;
          margin-top: 22px;
        }

        .crm-highlight-box {
          padding: 34px;
          border-radius: 22px;
          background: linear-gradient(145deg, #eff6ff, #ffffff);
          border: 1px solid #dbeafe;
        }

        .crm-highlight-box h3 {
          margin: 0 0 20px;
          font-size: 21px;
        }

        .crm-highlight-list {
          display: grid;
          gap: 11px;
        }

        .crm-highlight-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          border-radius: 10px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          color: #475569;
          font-size: 13px;
        }

        .crm-highlight-item span {
          color: #2563eb;
          font-weight: 800;
        }

        /* =========================
           FEATURES
        ========================= */

        .crm-feature-header {
          max-width: 780px;
          margin: 0 auto 45px;
          text-align: center;
        }

        .crm-feature-header .crm-title {
          margin-left: auto;
          margin-right: auto;
        }

        .crm-feature-header .crm-description {
          margin-left: auto;
          margin-right: auto;
        }

        .crm-feature-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        .crm-feature {
          min-height: 225px;
          padding: 25px;
          border-radius: 16px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          transition: .3s ease;
        }

        .crm-feature:hover {
          transform: translateY(-5px);
          border-color: #bfdbfe;
          box-shadow: 0 18px 40px rgba(15,23,42,.07);
        }

        .crm-feature-icon {
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          border-radius: 11px;
          background: #eff6ff;
          color: #2563eb;
          font-size: 18px;
          font-weight: 800;
        }

        .crm-feature h3 {
          margin: 0 0 9px;
          font-size: 16px;
        }

        .crm-feature p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.7;
        }

        /* =========================
           BENEFITS
        ========================= */

        .crm-benefit-grid {
          display: grid;
          grid-template-columns: .9fr 1.1fr;
          gap: 70px;
          align-items: start;
        }

        .crm-benefits {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 14px;
          margin-top: 5px;
        }

        .crm-benefit {
          padding: 22px;
          border-radius: 14px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
        }

        .crm-benefit-number {
          color: #2563eb;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1px;
        }

        .crm-benefit h3 {
          margin: 8px 0 7px;
          font-size: 15px;
        }

        .crm-benefit p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.7;
        }

        /* =========================
           WORKFLOW
        ========================= */

        .crm-workflow-header {
          text-align: center;
          max-width: 780px;
          margin: 0 auto 45px;
        }

        .crm-workflow-header .crm-title {
          margin-left: auto;
          margin-right: auto;
        }

        .crm-workflow-header .crm-description {
          margin-left: auto;
          margin-right: auto;
        }

        .crm-workflow {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }

        .crm-workflow-step {
          position: relative;
          padding: 22px 18px;
          border-radius: 14px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          text-align: center;
        }

        .crm-workflow-step:nth-child(-n+4)::after {
          content: "→";
          position: absolute;
          right: -15px;
          top: 50%;
          transform: translateY(-50%);
          color: #2563eb;
          font-weight: 800;
          z-index: 2;
        }

        .crm-workflow-step:nth-child(4)::after {
          display: none;
        }

        .crm-workflow-number {
          width: 34px;
          height: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 10px;
          border-radius: 10px;
          background: #eff6ff;
          color: #2563eb;
          font-size: 10px;
          font-weight: 800;
        }

        .crm-workflow-step strong {
          font-size: 12px;
        }

        /* =========================
           INDUSTRIES
        ========================= */

        .crm-industry-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 30px;
        }

        .crm-industry {
          padding: 11px 14px;
          border-radius: 10px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          color: #475569;
          font-size: 12px;
          font-weight: 600;
        }

        /* =========================
           FAQ
        ========================= */

        .crm-faq-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
          margin-top: 35px;
        }

        .crm-faq {
          padding: 24px;
          border-radius: 14px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
        }

        .crm-faq h3 {
          margin: 0 0 9px;
          font-size: 15px;
        }

        .crm-faq p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.75;
        }

        /* =========================
           CTA
        ========================= */

        .crm-cta {
          padding: 85px 0;
        }

        .crm-cta-box {
          position: relative;
          overflow: hidden;
          padding: 70px 30px;
          text-align: center;
          border-radius: 24px;
          color: #ffffff;
          background:
            radial-gradient(
              circle at 85% 15%,
              rgba(59,130,246,.30),
              transparent 30%
            ),
            linear-gradient(135deg, #0f172a, #172554);
        }

        .crm-cta-label {
          display: inline-block;
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.10);
          color: #bfdbfe;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .crm-cta h2 {
          max-width: 700px;
          margin: 20px auto 12px;
          font-size: clamp(34px, 4vw, 50px);
          line-height: 1.1;
          letter-spacing: -1.5px;
        }

        .crm-cta p {
          max-width: 650px;
          margin: 0 auto 28px;
          color: #cbd5e1;
          font-size: 15px;
          line-height: 1.8;
        }

        .crm-cta .crm-btn-primary {
          background: #ffffff;
          color: #0f172a;
        }

        .crm-cta .crm-btn-primary:hover {
          background: #eff6ff;
        }

        /* =========================
           SEO CONTENT
        ========================= */

        .crm-seo-content {
          padding: 0 0 75px;
        }

        .crm-seo-content-inner {
          max-width: 900px;
          margin: 0 auto;
        }

        .crm-seo-content h2 {
          margin: 0 0 15px;
          font-size: 25px;
          color: #0f172a;
        }

        .crm-seo-content h3 {
          margin: 28px 0 10px;
          font-size: 19px;
          color: #0f172a;
        }

        .crm-seo-content p {
          margin: 0 0 15px;
          color: #64748b;
          font-size: 14px;
          line-height: 1.85;
        }

        /* =========================
           RESPONSIVE
        ========================= */

        @media (max-width: 1000px) {
          .crm-hero-grid {
            grid-template-columns: 1fr;
            gap: 45px;
          }

          .crm-hero-visual {
            max-width: 600px;
          }

          .crm-feature-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .crm-intro-grid,
          .crm-benefit-grid {
            grid-template-columns: 1fr;
            gap: 45px;
          }

          .crm-workflow {
            grid-template-columns: repeat(2, 1fr);
          }

          .crm-workflow-step:nth-child(n)::after {
            display: none;
          }
        }

        @media (max-width: 750px) {
          .hexxa-container {
            width: min(100% - 28px, 1180px);
          }

          .crm-hero {
            padding: 75px 0 65px;
          }

          .crm-hero h1 {
            font-size: 42px;
            letter-spacing: -1.8px;
          }

          .crm-hero-text {
            font-size: 15px;
          }

          .crm-dashboard {
            transform: none;
          }

          .crm-overview-box {
            grid-template-columns: repeat(2, 1fr);
          }

          .crm-overview-item:nth-child(2) {
            border-right: 0;
          }

          .crm-overview-item:nth-child(-n+2) {
            border-bottom: 1px solid #e5e7eb;
          }

          .crm-section {
            padding: 65px 0;
          }

          .crm-feature-grid,
          .crm-benefits,
          .crm-faq-grid {
            grid-template-columns: 1fr;
          }

          .crm-workflow {
            grid-template-columns: 1fr;
          }

          .crm-dashboard-content {
            grid-template-columns: 55px 1fr;
          }

          .crm-sidebar {
            min-height: 230px;
          }

          .crm-mini-grid {
            grid-template-columns: 1fr;
          }

          .crm-cta {
            padding: 65px 0;
          }

          .crm-cta-box {
            padding: 50px 20px;
            border-radius: 18px;
          }
        }

        @media (max-width: 480px) {
          .crm-overview-box {
            grid-template-columns: 1fr;
          }

          .crm-overview-item {
            border-right: 0;
            border-bottom: 1px solid #e5e7eb;
          }

          .crm-overview-item:last-child {
            border-bottom: 0;
          }

          .crm-hero h1 {
            font-size: 38px;
          }

          .crm-buttons {
            flex-direction: column;
            align-items: stretch;
          }

          .crm-btn {
            width: 100%;
          }

          .crm-lead-status {
            display: none;
          }
        }
      `}</style>

      {/* =========================================
          HERO
      ========================================= */}

      <section className="crm-hero">
        <div className="hexxa-container crm-hero-grid">

          <div>
            <div className="crm-eyebrow">
              <span className="crm-eyebrow-dot"></span>
              CRM & Lead Management
            </div>

            <h1>
              Turn leads into
              <br />
              <span>stronger customer relationships.</span>
            </h1>

            <p className="crm-hero-text">
              HexxaTech develops custom CRM and lead management software
              designed around your sales process, customer interactions,
              follow-ups, appointments and business workflows.
            </p>

            <div className="crm-buttons">
              <a
                href="https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20would%20like%20to%20discuss%20a%20CRM%20and%20lead%20management%20software%20project."
                target="_blank"
                rel="noopener noreferrer"
                className="crm-btn crm-btn-primary"
                onClick={() =>
                  trackWhatsAppClick("crm_service_hero")
                }
              >
                Discuss Your CRM Project →
              </a>

              <Link
                to="/case-studies"
                className="crm-btn crm-btn-light"
                onClick={() =>
                  trackEventCustom("crm_case_studies_click", {
                    location: "crm_service_hero",
                  })
                }
              >
                View Case Studies →
              </Link>
            </div>
          </div>

          {/* CRM VISUAL */}

          <div className="crm-hero-visual">
            <div className="crm-dashboard">

              <div className="crm-dashboard-top">
                <span className="crm-dot"></span>
                <span className="crm-dot"></span>
                <span className="crm-dot"></span>
              </div>

              <div className="crm-dashboard-content">

                <div className="crm-sidebar">
                  <div className="crm-side-line active"></div>
                  <div className="crm-side-line"></div>
                  <div className="crm-side-line"></div>
                  <div className="crm-side-line"></div>
                  <div className="crm-side-line"></div>
                  <div className="crm-side-line"></div>
                </div>

                <div className="crm-dashboard-main">

                  <div className="crm-dashboard-title">
                    <strong>CRM Dashboard</strong>
                    <span>Sales Overview</span>
                  </div>

                  <div className="crm-mini-grid">

                    <div className="crm-mini-card">
                      <strong>248</strong>
                      <small>Total Leads</small>
                    </div>

                    <div className="crm-mini-card">
                      <strong>64</strong>
                      <small>Follow-ups</small>
                    </div>

                    <div className="crm-mini-card">
                      <strong>32</strong>
                      <small>Conversions</small>
                    </div>

                  </div>

                  <div className="crm-lead-list">

                    <div className="crm-lead">
                      <div className="crm-lead-avatar">
                        AM
                      </div>

                      <div className="crm-lead-info">
                        <strong>Acme Industries</strong>
                        <small>Follow-up scheduled</small>
                      </div>

                      <span className="crm-lead-status">
                        Active
                      </span>
                    </div>

                    <div className="crm-lead">
                      <div className="crm-lead-avatar">
                        RK
                      </div>

                      <div className="crm-lead-info">
                        <strong>RK Enterprises</strong>
                        <small>Quotation discussion</small>
                      </div>

                      <span className="crm-lead-status">
                        Hot
                      </span>
                    </div>

                    <div className="crm-lead">
                      <div className="crm-lead-avatar">
                        PS
                      </div>

                      <div className="crm-lead-info">
                        <strong>Prime Solutions</strong>
                        <small>New enquiry</small>
                      </div>

                      <span className="crm-lead-status">
                        New
                      </span>
                    </div>

                  </div>

                </div>

              </div>

            </div>
          </div>

        </div>
      </section>

      {/* =========================================
          OVERVIEW
      ========================================= */}

      <section className="crm-overview">
        <div className="hexxa-container">

          <div className="crm-overview-box">

            <div className="crm-overview-item">
              <div className="crm-overview-icon">◎</div>
              <strong>Lead Management</strong>
              <span>Centralized lead tracking</span>
            </div>

            <div className="crm-overview-item">
              <div className="crm-overview-icon">↗</div>
              <strong>Sales Pipeline</strong>
              <span>Track opportunities</span>
            </div>

            <div className="crm-overview-item">
              <div className="crm-overview-icon">✓</div>
              <strong>Follow-ups</strong>
              <span>Never miss an activity</span>
            </div>

            <div className="crm-overview-item">
              <div className="crm-overview-icon">▦</div>
              <strong>Reports</strong>
              <span>Actionable business insights</span>
            </div>

          </div>

        </div>
      </section>

      {/* =========================================
          INTRODUCTION
      ========================================= */}

      <section className="crm-section">

        <div className="hexxa-container crm-intro-grid">

          <div>

            <span className="crm-label">
              Custom CRM Development
            </span>

            <h2 className="crm-title">
              A CRM designed around the way your sales team actually works.
            </h2>

            <p className="crm-intro-text">
              Generic CRM platforms often force businesses to change their
              existing processes. HexxaTech takes a different approach.
              We build CRM and lead management systems around your actual
              sales workflow, customer journey, team structure and reporting
              requirements.
            </p>

            <p className="crm-intro-text">
              From capturing leads and assigning sales executives to managing
              follow-ups, appointments, quotations, customer interactions and
              conversions, every part of the system can be designed around
              your business.
            </p>

          </div>

          <div className="crm-highlight-box">

            <h3>
              What your CRM can manage
            </h3>

            <div className="crm-highlight-list">

              {[
                "Leads and enquiries",
                "Customers and contacts",
                "Sales representatives",
                "Follow-ups and reminders",
                "Appointments and meetings",
                "Sales opportunities",
                "Quotations and conversions",
                "Reports and dashboards",
              ].map((item) => (
                <div
                  className="crm-highlight-item"
                  key={item}
                >
                  <span>✓</span>
                  {item}
                </div>
              ))}

            </div>

          </div>

        </div>

      </section>

      {/* =========================================
          FEATURES
      ========================================= */}

      <section className="crm-section crm-section-soft">

        <div className="hexxa-container">

          <div className="crm-feature-header">

            <span className="crm-label">
              CRM Features
            </span>

            <h2 className="crm-title">
              Everything your team needs to manage customers and leads.
            </h2>

            <p className="crm-description">
              Build a centralized CRM platform that gives your sales team
              visibility across leads, customers, follow-ups, activities
              and opportunities.
            </p>

          </div>

          <div className="crm-feature-grid">

            {features.map((feature) => (
              <article
                className="crm-feature"
                key={feature.title}
                onClick={() =>
                  trackEventCustom("crm_feature_interest", {
                    feature: feature.title,
                  })
                }
              >

                <div className="crm-feature-icon">
                  {feature.icon}
                </div>

                <h3>
                  {feature.title}
                </h3>

                <p>
                  {feature.text}
                </p>

              </article>
            ))}

          </div>

        </div>

      </section>

      {/* =========================================
          BENEFITS
      ========================================= */}

      <section className="crm-section">

        <div className="hexxa-container crm-benefit-grid">

          <div>

            <span className="crm-label">
              Business Benefits
            </span>

            <h2 className="crm-title">
              More visibility. Better follow-ups. Stronger sales operations.
            </h2>

            <p className="crm-description">
              A well-designed CRM gives your team a single source of truth
              for customer information and sales activities while helping
              management understand what is happening across the sales
              pipeline.
            </p>

          </div>

          <div className="crm-benefits">

            {benefits.map((benefit, index) => (
              <div
                className="crm-benefit"
                key={benefit.title}
              >

                <div className="crm-benefit-number">
                  {String(index + 1).padStart(2, "0")}
                </div>

                <h3>
                  {benefit.title}
                </h3>

                <p>
                  {benefit.text}
                </p>

              </div>
            ))}

          </div>

        </div>

      </section>

      {/* =========================================
          WORKFLOW
      ========================================= */}

      <section className="crm-section crm-section-soft">

        <div className="hexxa-container">

          <div className="crm-workflow-header">

            <span className="crm-label">
              CRM Workflow
            </span>

            <h2 className="crm-title">
              From first enquiry to customer conversion.
            </h2>

            <p className="crm-description">
              Your CRM can connect every important stage of the customer
              journey and give your team visibility from lead generation
              through conversion.
            </p>

          </div>

          <div className="crm-workflow">

            {workflow.map((item, index) => (
              <div
                className="crm-workflow-step"
                key={item}
              >

                <div className="crm-workflow-number">
                  {String(index + 1).padStart(2, "0")}
                </div>

                <strong>
                  {item}
                </strong>

              </div>
            ))}

          </div>

        </div>

      </section>

      {/* =========================================
          INDUSTRIES
      ========================================= */}

      <section className="crm-section">

        <div className="hexxa-container">

          <span className="crm-label">
            Industries We Serve
          </span>

          <h2 className="crm-title">
            CRM solutions for different business models.
          </h2>

          <p className="crm-description">
            Because every sales process is different, our CRM systems can
            be customized around the requirements of different industries
            and organizations.
          </p>

          <div className="crm-industry-grid">

            {industries.map((industry) => (
              <span
                className="crm-industry"
                key={industry}
              >
                {industry}
              </span>
            ))}

          </div>

        </div>

      </section>

      {/* =========================================
          FAQ
      ========================================= */}

      <section className="crm-section crm-section-soft">

        <div className="hexxa-container">

          <span className="crm-label">
            Frequently Asked Questions
          </span>

          <h2 className="crm-title">
            Common questions about custom CRM development.
          </h2>

          <div className="crm-faq-grid">

            <div className="crm-faq">
              <h3>
                What is custom CRM software?
              </h3>

              <p>
                Custom CRM software is a customer relationship management
                system developed specifically around a company's sales
                process, customer data, workflows and reporting requirements.
              </p>
            </div>

            <div className="crm-faq">
              <h3>
                Can you migrate data from Excel or an existing CRM?
              </h3>

              <p>
                Yes. Depending on the existing data structure, customer,
                lead, follow-up and other business information can be
                migrated into the new CRM.
              </p>
            </div>

            <div className="crm-faq">
              <h3>
                Can the CRM include lead assignment?
              </h3>

              <p>
                Yes. Lead assignment can be designed around your team
                structure, territories, departments, products or other
                business rules.
              </p>
            </div>

            <div className="crm-faq">
              <h3>
                Can CRM software integrate with other systems?
              </h3>

              <p>
                Yes. Custom CRM systems can integrate with APIs, websites,
                mobile applications, ERP systems, databases and other
                business platforms where required.
              </p>
            </div>

            <div className="crm-faq">
              <h3>
                Can management dashboards be included?
              </h3>

              <p>
                Yes. Custom dashboards can display lead counts, conversion
                rates, follow-ups, sales activities, appointments and other
                business metrics.
              </p>
            </div>

            <div className="crm-faq">
              <h3>
                Does HexxaTech provide CRM support?
              </h3>

              <p>
                Yes. We can provide ongoing technical support, maintenance,
                improvements and additional modules as your business grows.
              </p>
            </div>

          </div>

        </div>

      </section>

      {/* =========================================
          SERVICE NAVIGATION
      ========================================= */}

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            width: "min(100% - 32px, 1100px)",
            margin: "0 auto",
            maxWidth: "1100px",
          }}
        >
          <span className="crm-label">
            Explore Our Services
          </span>

          <h2 className="crm-title" style={{ marginTop: "12px" }}>
            Explore our other software development services.
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
              marginTop: "22px",
            }}
          >
            {serviceNavigation.map((service) => {
              const isCurrent = service.to === "/services/crm-lead-management";

              return (
                <Link
                  key={service.to}
                  to={service.to}
                  style={{
                    display: "block",
                    padding: "16px 18px",
                    borderRadius: "12px",
                    textDecoration: "none",
                    border: isCurrent ? "1px solid #2563eb" : "1px solid #e5e7eb",
                    background: isCurrent ? "#eff6ff" : "#f8fafc",
                    color: isCurrent ? "#1d4ed8" : "#334155",
                    fontWeight: isCurrent ? 700 : 600,
                  }}
                >
                  {service.title}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            width: "min(100% - 32px, 1100px)",
            margin: "0 auto",
            maxWidth: "1100px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
              borderTop: "1px solid #e5e7eb",
              paddingTop: "20px",
            }}
          >
            <Link
              to={previousService.to}
              style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}
            >
              ← Previous: {previousService.title}
            </Link>

            <Link
              to={nextService.to}
              style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}
            >
              Next: {nextService.title} →
            </Link>
          </div>
        </div>
      </section>

      {/* =========================================
          CTA
      ========================================= */}

      <section className="crm-cta">

        <div className="hexxa-container">

          <div className="crm-cta-box">

            <span className="crm-cta-label">
              Build Your CRM
            </span>

            <h2>
              Ready to organize your leads and sales process?
            </h2>

            <p>
              Tell us how your sales team currently manages leads,
              customers and follow-ups. We can help design a CRM solution
              around your actual business workflow.
            </p>

            <a
              href="https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20would%20like%20to%20discuss%20a%20CRM%20and%20lead%20management%20software%20project."
              target="_blank"
              rel="noopener noreferrer"
              className="crm-btn crm-btn-primary"
              onClick={() =>
                trackWhatsAppClick("crm_service_cta")
              }
            >
              Discuss Your CRM Project →
            </a>

          </div>

        </div>

      </section>

      {/* =========================================
          SEO CONTENT
      ========================================= */}

      <section className="crm-seo-content">

        <div className="hexxa-container crm-seo-content-inner">

          <h2>
            CRM & Lead Management Software Development Company in Pune
          </h2>

          <p>
            HexxaTech Softwares develops custom CRM and lead management
            software for businesses in Pune and across India. Our CRM
            solutions help organizations manage leads, customers,
            follow-ups, appointments, sales activities, opportunities,
            quotations, conversions and business relationships from a
            centralized platform.
          </p>

          <p>
            Instead of forcing your organization to adapt to generic CRM
            software, we build the system around your existing sales
            process. This allows businesses to create workflows that match
            their team structure, customer journey, approval processes,
            reporting requirements and operational needs.
          </p>

          <h3>
            Custom CRM Software for Business Growth
          </h3>

          <p>
            A custom CRM can help businesses centralize customer information,
            improve follow-up management, track sales opportunities and give
            management better visibility into sales operations. CRM modules
            can be developed according to the specific requirements of
            manufacturing companies, automotive businesses, healthcare
            organizations, service providers, distributors, educational
            organizations and other businesses.
          </p>

          <h3>
            Lead Management Software in Pune
          </h3>

          <p>
            Our lead management solutions can capture enquiries, assign leads
            to sales representatives, track follow-ups, record customer
            interactions and monitor conversion progress. Businesses can
            also use dashboards and reports to understand lead sources,
            sales activities and pipeline performance.
          </p>

          <h3>
            CRM Development Services Across India
          </h3>

          <p>
            HexxaTech Softwares provides custom CRM development services for
            businesses in Pune and across India. Our approach focuses on
            understanding the business process first and then designing the
            software architecture, database, user interface, workflows,
            integrations and reporting around those requirements.
          </p>

        </div>

      </section>
    </div>
  );
}